import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  Stack,
  Typography,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import CloseIcon from '@mui/icons-material/Close';
import { memo, useState, useCallback, useEffect, useMemo } from 'react';
import type { GroupSettingsData } from '../hooks/useGroupSettings';
import OrganizerSelector from './OrganizerSelector';
import { useGroupMembers } from '../hooks/useGroupMembers';
import { useGroupPosterManagement } from '../../../hooks/useGroupPosterManagement';

export type GroupSettingsTabProps = {
  readonly group: any; // Group with current settings
  readonly loading: boolean;
  readonly saving: boolean;
  readonly error: Error | null;
  readonly validationErrors: Record<string, string[]> | null;
  readonly hasUnsavedChanges: boolean;
  readonly onUpdateSettings: (settings: Partial<GroupSettingsData>) => Promise<void>;
  readonly onResetForm: () => void;
  readonly onDeleteGroup?: () => Promise<void>;
  readonly isOrganizer: boolean;
};

const GroupSettingsTab = memo<GroupSettingsTabProps>(
  ({
    group,
    loading,
    saving,
    error,
    validationErrors,
    hasUnsavedChanges,
    onUpdateSettings,
    onResetForm,
    onDeleteGroup,
    isOrganizer,
  }) => {
    const [formData, setFormData] = useState<Partial<GroupSettingsData>>({});
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [posterFile, setPosterFile] = useState<File | null>(null);
    const [posterAltText, setPosterAltText] = useState<string>('');
    const [posterUploadSuccess, setPosterUploadSuccess] = useState(false);
    const [posterUploadError, setPosterUploadError] = useState<string | null>(null);

    // Fetch group members for organizer selection
    const { members: groupMembers, loading: membersLoading } = useGroupMembers(
      group?.id || null,
    );

    // Group poster management
    const {
      uploadPoster,
      removePoster,
      isUploading: posterUploading,
      progress: posterProgress,
      error: posterError,
    } = useGroupPosterManagement({ groupId: group?.id || '' });

    // Log whenever group prop changes (including poster fields)
    useEffect(() => {
      console.log('🎨 GroupSettingsTab: Group prop changed (ANY field):', {
        id: group?.id,
        hasPosterUrl: !!group?.posterUrl,
        posterUrl: group?.posterUrl,
      });
    }, [group]);

    // Initialize form data when group loads (only when group ID changes)
    useEffect(() => {
      if (group) {
        console.log('🎨 GroupSettingsTab: Group prop updated:', {
          id: group.id,
          name: group.name,
          hasPosterUrl: !!group.posterUrl,
          posterUrl: group.posterUrl,
          posterStoragePath: group.posterStoragePath,
          posterAlt: group.posterAlt,
        });
        setFormData({
          name: group.name || '',
          summary: group.summary || '',
          autoAcceptMembers: group.autoAcceptMembers ?? false,
          organizerUid: group.organizerUid || '',
          organizerName: group.organizerName || '',
        });
      }
    }, [group?.id]); // Only re-initialize when group ID changes, not on every field update

    const handleFieldChange = useCallback(
      (field: keyof GroupSettingsData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
      },
      [],
    );

    // Calculate local unsaved changes by comparing formData with original group
    const localHasUnsavedChanges = useMemo(() => {
      if (!group || Object.keys(formData).length === 0) return false;

      // Helper to compare values, treating undefined and empty string as equal
      const hasChanged = (formValue: any, groupValue: any) => {
        // Treat empty string and undefined as the same
        const normalizedFormValue = formValue === '' ? undefined : formValue;
        const normalizedGroupValue = groupValue === '' ? undefined : groupValue;
        return normalizedFormValue !== normalizedGroupValue;
      };

      return (
        hasChanged(formData.name, group.name) ||
        hasChanged(formData.summary, group.summary) ||
        formData.autoAcceptMembers !== group.autoAcceptMembers ||
        hasChanged(formData.organizerUid, group.organizerUid) ||
        hasChanged(formData.organizerName, group.organizerName)
      );
    }, [formData, group]);

    const handleSubmit = useCallback(async () => {
      await onUpdateSettings(formData);
    }, [formData, onUpdateSettings]);

    const handleReset = useCallback(() => {
      // Reset local form data to original group values
      if (group) {
        setFormData({
          name: group.name || '',
          summary: group.summary || '',
          autoAcceptMembers: group.autoAcceptMembers ?? false,
          organizerUid: group.organizerUid || '',
          organizerName: group.organizerName || '',
        });
      }
      // Also call parent's reset in case it has additional logic
      onResetForm();
    }, [group, onResetForm]);

    const handleDeleteClick = useCallback(() => {
      setDeleteDialogOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
      if (!onDeleteGroup) return;

      setDeleting(true);
      try {
        await onDeleteGroup();
        setDeleteDialogOpen(false);
      } catch (err) {
        console.error('Failed to delete group:', err);
        setDeleting(false);
      }
    }, [onDeleteGroup]);

    const handleDeleteCancel = useCallback(() => {
      setDeleteDialogOpen(false);
    }, []);

    const handlePosterFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        // Validate file type and size
        if (!file.type.startsWith('image/')) {
          alert('Please select an image file');
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          alert('Image must be less than 5MB');
          return;
        }
        setPosterFile(file);
      }
    }, []);

    const handlePosterUpload = useCallback(async () => {
      if (!posterFile) return;

      setPosterUploadError(null);
      setPosterUploadSuccess(false);

      try {
        await uploadPoster(posterFile, posterAltText);
        setPosterFile(null);
        setPosterAltText('');
        setPosterUploadSuccess(true);

        // Clear success message after 3 seconds
        setTimeout(() => {
          setPosterUploadSuccess(false);
        }, 3000);
      } catch (error) {
        console.error('Failed to upload poster:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to upload poster. Please try again.';
        setPosterUploadError(errorMessage);
      }
    }, [posterFile, posterAltText, uploadPoster]);

    const handlePosterRemove = useCallback(async () => {
      if (!group?.posterStoragePath) return;

      if (confirm('Are you sure you want to remove the group poster?')) {
        try {
          await removePoster(group.posterStoragePath);
        } catch (error) {
          console.error('Failed to remove poster:', error);
        }
      }
    }, [group?.posterStoragePath, removePoster]);

    const getFieldError = (field: string): string | undefined => {
      return validationErrors?.[field]?.[0];
    };

    if (!isOrganizer) {
      return <Alert severity="info">Only organizers can modify group settings.</Alert>;
    }

    if (loading) {
      return <Typography color="text.secondary">Loading settings...</Typography>;
    }

    if (!group) {
      return <Alert severity="error">Group not found</Alert>;
    }

    return (
      <Stack spacing={3}>
        <Typography variant="h6">Group Settings</Typography>

        {error && <Alert severity="error">{error.message}</Alert>}

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>

              {/* Group Name */}
              <TextField
                label="Group Name"
                value={formData.name || ''}
                onChange={e => handleFieldChange('name', e.target.value)}
                error={!!getFieldError('name')}
                helperText={getFieldError('name') || 'The public name for your group'}
                fullWidth
                required
              />

              {/* Group Summary */}
              <TextField
                label="Summary"
                value={formData.summary || ''}
                onChange={e => handleFieldChange('summary', e.target.value)}
                error={!!getFieldError('summary')}
                helperText={
                  getFieldError('summary') || 'A brief description of your group'
                }
                multiline
                rows={3}
                fullWidth
              />

              {/* Organizer */}
              <OrganizerSelector
                uid={formData.organizerUid || ''}
                organizerName={formData.organizerName || ''}
                members={groupMembers}
                onSelect={(uid, displayName) => {
                  handleFieldChange('organizerUid', uid);
                  handleFieldChange('organizerName', displayName);
                }}
                error={getFieldError('organizerUid')}
                disabled={saving}
                loading={membersLoading}
              />

              <Divider />

              <Typography variant="h6" gutterBottom>
                Group Poster
              </Typography>

              {/* Current Poster Display */}
              {group?.posterUrl && !posterFile && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Current Poster:
                  </Typography>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Box
                      component="img"
                      src={group.posterUrl}
                      alt={group.posterAlt || 'Group poster'}
                      sx={{
                        width: '100%',
                        maxWidth: 400,
                        height: 'auto',
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider',
                      }}
                    />
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      startIcon={<CloseIcon />}
                      onClick={handlePosterRemove}
                      disabled={posterUploading}
                      sx={{ mt: 1 }}
                    >
                      Remove Poster
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Poster Upload */}
              <Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="group-poster-upload"
                  type="file"
                  onChange={handlePosterFileSelect}
                  disabled={posterUploading}
                />
                <label htmlFor="group-poster-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    disabled={posterUploading}
                    fullWidth
                  >
                    {posterFile
                      ? `Selected: ${posterFile.name}`
                      : group?.posterUrl
                        ? 'Replace Group Poster'
                        : 'Upload Group Poster'}
                  </Button>
                </label>

                {posterFile && (
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    <TextField
                      label="Poster Description (Alt Text)"
                      value={posterAltText}
                      onChange={e => setPosterAltText(e.target.value)}
                      placeholder="Describe the poster for accessibility"
                      helperText="Help screen readers understand your poster content"
                      fullWidth
                      multiline
                      rows={2}
                    />
                    <Button
                      variant="contained"
                      onClick={handlePosterUpload}
                      disabled={posterUploading}
                      startIcon={<UploadIcon />}
                    >
                      {posterUploading ? 'Uploading...' : 'Upload Poster'}
                    </Button>
                  </Stack>
                )}

                {posterUploading && posterProgress && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={posterProgress.percentage}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Uploading: {posterProgress.percentage}%
                    </Typography>
                  </Box>
                )}

                {posterError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {posterError.message}
                  </Alert>
                )}

                {posterUploadError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {posterUploadError}
                  </Alert>
                )}

                {posterUploadSuccess && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Poster uploaded successfully!
                  </Alert>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Maximum file size: 5MB. Supported formats: JPG, PNG, WebP
                </Typography>
              </Box>

              <Divider />

              <Typography variant="h6" gutterBottom>
                Membership Settings
              </Typography>

              {/* Auto Accept Members */}
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoAcceptMembers || false}
                    onChange={e =>
                      handleFieldChange('autoAcceptMembers', e.target.checked)
                    }
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Auto-accept new members</Typography>
                    <Typography variant="body2" color="text.secondary">
                      When enabled, membership requests are automatically approved. When
                      disabled, organizers must manually approve each request.
                    </Typography>
                  </Box>
                }
              />

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  disabled={saving || !localHasUnsavedChanges}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<UndoIcon />}
                  onClick={handleReset}
                  disabled={saving || !localHasUnsavedChanges}
                >
                  Reset
                </Button>
              </Stack>

              {localHasUnsavedChanges && (
                <Alert severity="info">
                  You have unsaved changes. Don't forget to save!
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        {onDeleteGroup && (
          <Card sx={{ borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6" color="error">
                  Danger Zone
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Once you delete a group, there is no going back. This will permanently
                  delete the group, all members, and all associated meetings.
                </Typography>
                <Box>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteClick}
                    disabled={saving || deleting}
                  >
                    Delete Group
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Group?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete <strong>{group?.name}</strong>?
              <br />
              <br />
              This action cannot be undone. All members and meetings associated with this
              group will be permanently deleted.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleting}
              autoFocus
            >
              {deleting ? 'Deleting...' : 'Delete Group'}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  },
);

GroupSettingsTab.displayName = 'GroupSettingsTab';

export default GroupSettingsTab;
